// --- React & Redux
import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import DataGrid from '@dtci/spellbook/DataGrid';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- API
import { datastageApi } from 'datastage/api';
// --- Hooks & Store
import { useAgGridApi } from 'datastage/components/AGGrid/hooks';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useTemplatesActions } from 'datastage/store/templates/useTemplates';
import { useToasts } from 'datastage/utils/hooks/useToasts';
// --- Config & Components
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
import NameCellEditor from 'datastage/components/AGGrid/cell-renderers/editor';
// --- Styles
import styles from 'datastage/components/AGGrid/details-table.module.scss';
import './styles.scss';

/**
 * Extract unique values from array and format as select options
 */
const getUniqueFilterOptions = (items, key) =>
    Array.from(new Set(items.map((t) => t[key]).filter(Boolean)))
        .map((v) => ({ label: String(v), value: v }));

/**
 * Saved Templates Grid
 *
 * Features:
 * - Displays a data grid of saved templates using AG Grid
 * - Allows filtering by action, module, template, and search
 * - Supports loading and deleting templates
 *
 * @returns {Element}
 * @constructor
 */
export const SavedTemplatesGrid = () => {
    const agGridApi = useAgGridApi();
    const { templates, actionOptions, entityTypeOptions, dateFormatOptions } = useSelector(
        (s) => s.templates,
    );
    const { updateTemplates, updateTemplatesForm } = useTemplatesActions();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showSuccessToast, showErrorToast } = useToasts();

    const [search, setSearch] = useState('');
    const [action, setAction] = useState(null);
    const [module, setModule] = useState(null);
    const [template, setTemplate] = useState(null);

    // Load template details and update state
    const onLoadTemplate = useCallback(async (data) => {
        setLoadingTrue();
        try {
            // Fetch template options first
            const templateOptions = await datastageApi.rlTemplatesByType(data?.moduleKey);

            // Match selections
            const matchedAction = actionOptions.find(
                (option) => String(option.value) === String(data?.actionKey),
            );
            const matchedEntity = entityTypeOptions.find(
                (option) => String(option.value) === String(data?.moduleKey),
            );
            const matchedTemplate = templateOptions.find(
                (option) => String(option.value) === String(data?.templateKey),
            );
            const matchedDateFormat = dateFormatOptions.find(
                (option) => String(option.value) === String(data?.dateFormatKey),
            );

            if (!matchedAction || !matchedEntity || !matchedTemplate || !matchedDateFormat) return;

            const uploadForm = {
                action: matchedAction,
                entityType: matchedEntity,
                template: matchedTemplate,
                dateFormat: matchedDateFormat,
                includeAllFields: false,
            };

            updateTemplatesForm({
                key: 'uploadForm',
                data: uploadForm,
            });

            const [fieldGroups, customFieldList] = await Promise.all([
                datastageApi.rlTemplateFields(uploadForm),
                datastageApi.rlCustomListSearch(uploadForm),
            ]);

            // Filter and map custom fields
            const defaultCustomFields = fieldGroups
                .flatMap((v) => v.fields)
                .filter(
                    (field) => Array.isArray(data.fieldKeys) && data.fieldKeys.includes(field.fieldId),
                )
                .map((field) => field.fieldId);

            // Find selected custom field list
            const matchCustomFieldList = customFieldList.find(
                (option) => String(option.value) === String(data?.id),
            );

            updateTemplates({
                isSavedTemplatesOpen: false,
                fieldGroups,
                customFields: defaultCustomFields,
                customFieldList,
                selectedCustomList: matchCustomFieldList,
                templateOptions,
            });
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error loading template.');
            console.error('Unexpected error loading template:', error);
        } finally {
            setLoadingFalse();
        }
    }, [actionOptions, entityTypeOptions, dateFormatOptions, updateTemplatesForm, updateTemplates, setLoadingTrue, setLoadingFalse, showErrorToast]);

    // Delete template and refresh list
    const onDeleteTemplate = useCallback(async (data) => {
        setLoadingTrue();
        try {
            const res = await datastageApi.deleteRLCustomList(data?.id);

            if (res?.code) {
                showErrorToast(res?.message || 'Failed to delete template.');
                return;
            }

            const updatedTemplates = await datastageApi.savedTemplates();

            updateTemplates({
                templates: updatedTemplates,
            });

            showSuccessToast('Template deleted successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error deleting template.');
            console.error('Unexpected error deleting template:', error);
        } finally {
            setLoadingFalse();
        }
    }, [updateTemplates, setLoadingTrue, setLoadingFalse, showSuccessToast, showErrorToast]);

    const columns = useMemo(() => {
        const defs = columnDefs['templates'](onLoadTemplate, onDeleteTemplate)['default'];
        const nameCol = defs.find((col) => col.field === 'name');
        if (nameCol) {
            nameCol.editable = true;
            nameCol.cellEditor = NameCellEditor;
        }
        return defs;
    }, [onLoadTemplate, onDeleteTemplate]);

    // Extract unique values for filter dropdowns
    const actionFilterOptions = useMemo(() => 
        getUniqueFilterOptions(templates, 'actionShortName'), [templates]);
    const moduleFilterOptions = useMemo(() => 
        getUniqueFilterOptions(templates, 'moduleName'), [templates]);
    const templateFilterOptions = useMemo(() => 
        getUniqueFilterOptions(templates, 'templateName'), [templates]);

    // Filter templates based on selections and search
    const filteredTemplates = useMemo(() => {
        return templates.filter((t) => {
            const matchesAction = action ? t.actionShortName === action.value : true;
            const matchesModule = module ? t.moduleName === module.value : true;
            const matchesTemplate = template ? t.templateName === template.value : true;
            const matchesSearch = search
                ? [
                      t.name,
                      t.dateFormatName,
                      Array.isArray(t.fieldKeys)
                          ? t.fieldKeys.map(String).join(', ')
                          : String(t.fieldKeys),
                      t.createdAt,
                      t.updatedAt,
                  ].some((v) => v && String(v).toLowerCase().includes(search.toLowerCase()))
                : true;
            return matchesAction && matchesModule && matchesTemplate && matchesSearch;
        });
    }, [templates, action, module, template, search]);

    // Handle saving updated template name
    const handleSave = useCallback(async (newValue, rowData) => {
        setLoadingTrue();

        try {
            const res = await datastageApi.saveRLCustomList({
                id: rowData?.id,
                name: newValue,
            });

            if (res?.code) {
                showErrorToast(res?.message || 'Failed to update template name.');
                return;
            }

            const data = await datastageApi.savedTemplates();

            updateTemplates({
                templates: data,
            });

            showSuccessToast('Template name updated successfully.');
        } catch (error) {
            showErrorToast(error?.message || 'Unexpected error saving template.');
            console.error('Unexpected error saving template:', error);
        } finally {
            setLoadingFalse();
        }
    }, [updateTemplates, setLoadingTrue, setLoadingFalse, showSuccessToast, showErrorToast]);

    return (
        <>
            <div className="saved-templates-filters">
                <Input
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size={'large'}
                />
                <Select
                    isClearable
                    isSearchable
                    options={actionFilterOptions}
                    placeholder="Action"
                    value={action}
                    onChange={setAction}
                    style={{ minWidth: 160 }}
                />
                <Select
                    isClearable
                    isSearchable
                    options={moduleFilterOptions}
                    placeholder="Module"
                    value={module}
                    onChange={setModule}
                    style={{ minWidth: 160 }}
                />
                <Select
                    isClearable
                    isSearchable
                    options={templateFilterOptions}
                    placeholder="Template"
                    value={template}
                    onChange={setTemplate}
                    style={{ minWidth: 160 }}
                />
            </div>
            <DataGrid
                className={styles.dataGrid}                
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: filteredTemplates,
                    headerHeight: 45,
                    context: {
                        onSave: handleSave,
                    },
                    defaultColDef: {
                        wrapText: true,
                        autoHeight: true,
                        suppressMenu: true,
                        resizable: true,
                        cellStyle: { 
                            paddingTop: '10px', 
                            paddingBottom: '10px',
                            wordBreak: 'normal',
                            overflowWrap: 'break-word',
                        },
                    },
                }}
                bordered="none"
                columnDefs={columns}
                sortable
                striped
            />
        </>
    );
};
